import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';
import {
  LEARNING_REVIEW_TITLE, INCIDENT_GRADE_LABEL, SELECT_REVIEW_INCIDENT,
  NO_SUBMITTED_INCIDENTS, REVIEW_SECTION_CORRECT, REVIEW_SECTION_MISSED,
  REVIEW_SECTION_COLLATERAL, REVIEW_SECTION_ACCEPTABLE,
  REVIEW_SECTION_ATTEMPTS, REVIEW_SECTION_DETECTIONS,
  REVIEW_SECTION_PLAYBOOK, REVIEW_SECTION_TAKEAWAY,
  REVIEW_SECTION_EXPECTED, REVIEW_EMPTY_EXPECTED,
  EXPECTED_REQUIRED_LABEL, EXPECTED_ACCEPTABLE_LABEL, INACTION_ZERO_HEADING,
  correctDetectionCalls, NO_CORRECT_CALLS, NO_COMPLETED_REQUIRED,
  REVIEW_EMPTY_MISSED, REVIEW_EMPTY_COLLATERAL, REVIEW_EMPTY_DETECTIONS,
  BREAKDOWN_LOAD_ERROR, CALL_CORRECT, CALL_WRONG, ACTION_LABELS,
} from './uiCopy';
import { deriveAchievements } from './achievements';
import { CARD_STYLE, gradeColor } from './ui';

// Stage 5 Phase 5 commit 5.4 (ratified B-OD-1 Option 1): the Metrics tab is
// the ONE durable per-incident Learning Review home. Everything rendered
// here reads the SUBMITTED score view (the frozen record served across the
// submission boundary) plus the submitted-only triage review -- teaching is
// post-boundary by construction, and the in-workspace Case Closed modal
// never renders teaching content (one venue, 19.19). Labeling keeps the
// 3.9B distinction: this block is Incident Grade, never Session Performance.

// Visual pass VG: card surface + grade colors from the shared module.

// One teaching entry row: verb + target + the frozen why.
const EntryRow = ({ e }) => (
  <li className="text-sm">
    {e.action ? (
      <p className="text-[#1a2332]">
        <span className="font-medium">{ACTION_LABELS[e.action] || e.action}</span>
        {e.target_label ? <span className="log-mono text-muted"> {e.target_label}</span> : null}
      </p>
    ) : null}
    <p className="text-muted">{e.why}</p>
  </li>
);

const Section = ({ title, entries, emptyLine }) => (
  <div className="pt-3 border-t border-[#eef1f4]">
    <p className="t-subsection mb-1.5">{title}</p>
    {entries.length === 0 ? (
      <p className="text-sm text-muted">{emptyLine}</p>
    ) : (
      <ul className="space-y-2">{entries.map((e, i) => <EntryRow key={i} e={e} />)}</ul>
    )}
  </div>
);

const AchievementChips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="achievements">
    {items.map(a => (
      <span key={a.key} className="inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded-full text-[11px] bg-[#eef1f4] text-[#1a2332] border border-[#d0d7de]">
        <span className="font-medium">{a.label}</span>
        {a.subtitle && <span className="text-muted">{a.subtitle}</span>}
      </span>
    ))}
  </div>
);

const LearningReview = ({ reviewRequest, isVisible = true }) => {
  const [completed, setCompleted] = useState([]);   // submitted incident cards
  const [selectedId, setSelectedId] = useState(null);
  const [scoreView, setScoreView] = useState(null); // the served frozen record view
  const [triage, setTriage] = useState(null);       // submitted-only educational content
  const [loadError, setLoadError] = useState(false);

  // The submitted list feeds the per-incident selector.
  const fetchList = useCallback(() => {
    apiFetch('/api/incidents').then(r => r.json())
      .then(d => setCompleted(d.completed || []))
      .catch(() => {});
  }, []);
  useEffect(() => { fetchList(); const iv = setInterval(fetchList, 3000); return () => clearInterval(iv); }, [fetchList]);

  // "Review what you learned" lands here with an incident preselected.
  useEffect(() => {
    if (reviewRequest?.id) setSelectedId(reviewRequest.id);
  }, [reviewRequest]);

  const fetchDetail = useCallback(() => {
    if (!selectedId) { setScoreView(null); setTriage(null); setLoadError(false); return; }
    setLoadError(false);
    apiFetch(`/api/incidents/${selectedId}/score`)
      .then(r => { if (!r.ok) throw new Error('score'); return r.json(); })
      .then(v => { setScoreView(v?.state === 'submitted' ? v : null); })
      .catch(() => { setScoreView(null); setLoadError(true); });
    apiFetch(`/api/incidents/${selectedId}/triage-review`)
      .then(r => (r.ok ? r.json() : null))
      .then(setTriage)
      .catch(() => setTriage(null));
  }, [selectedId]);
  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const grading = scoreView?.grading || null;
  const review = grading?.response_review || null;
  const achievements = deriveAchievements(scoreView);
  const selectedCard = completed.find(c => c.incident_id === selectedId) || null;

  // Bucket routing (7.1): inaction_correct answers "what you did
  // correctly"; inaction_spoiled belongs with what you missed; the
  // collateral entries themselves carry the harm.
  const entries = review?.entries || [];
  const correctEntries = entries.filter(e => e.bucket === 'completed' || e.reason_code === 'inaction_correct');
  const missedEntries = entries.filter(e => e.bucket === 'missed' || e.reason_code === 'inaction_spoiled');
  const collateralEntries = entries.filter(e => e.bucket === 'collateral');
  const acceptableEntries = entries.filter(e => e.bucket === 'acceptable');
  // C1 checkpoint fix (F5a): the well bucket reads BOTH ratified halves --
  // the frozen per-detection verdicts feed the correct-calls line.
  const detectionCalls = review?.detections || [];
  const correctCallCount = detectionCalls.filter(d => d.correct).length;

  return (
    // VF (section 5): Dashboard card anatomy -- the section title and its
    // Incident Grade label live INSIDE the bordered container, above the
    // locked-state message / picker / review content. One flat card.
    <div className="rounded-xl p-4 sm:p-6" style={CARD_STYLE}>
      {/* Owner UI pass (2026-10-12): the Incident Grade tag beside the
          section title is removed; the 3.9B labeling distinction stays
          carried by the detail overline and the Composite row below. */}
      <div className="mb-4">
        <h2 className="t-section">{LEARNING_REVIEW_TITLE}</h2>
      </div>
      <div>
        {completed.length === 0 ? (
          <p className="text-sm text-muted">{NO_SUBMITTED_INCIDENTS}</p>
        ) : (
          <div className="space-y-4">
            {/* Per-incident selector (submitted incidents only) */}
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Submitted incidents">
              {completed.map(c => (
                <button key={c.incident_id} type="button"
                  onClick={() => setSelectedId(c.incident_id)}
                  className={`px-2.5 py-1 rounded-md text-xs border transition ${c.incident_id === selectedId
                    ? 'bg-[#101218] text-white border-transparent'
                    : 'bg-white text-muted border-[#d0d7de] hover:bg-[#eef1f4]'}`}>
                  {/* VD8: incident ids read in Inter inside identity
                      controls, matching the badge system */}
                  <span>{c.incident_id}</span>
                  <span className="ml-1.5">{c.incident_grade?.grade || '-'}</span>
                </button>
              ))}
            </div>

            {!selectedId ? (
              <p className="text-sm text-muted">{SELECT_REVIEW_INCIDENT}</p>
            ) : loadError || (scoreView && !review) ? (
              // 7.1 loading/error state (also the degraded-render guard when
              // a served grading lacks the breakdown): grade rows if we have
              // them, the one-line error, and a retry.
              <div className="space-y-3">
                {grading && (
                  <div className="text-sm text-muted">
                    {INCIDENT_GRADE_LABEL}: <span className="font-semibold" style={{ color: gradeColor(grading.composite?.grade) }}>{grading.composite?.grade || '-'} · {grading.composite?.accuracy ?? '-'}%</span>
                  </div>
                )}
                <p className="text-sm text-[#b45858]">{BREAKDOWN_LOAD_ERROR}</p>
                <button type="button" onClick={fetchDetail}
                  className="px-3 py-1.5 text-sm rounded-md border border-[#d0d7de] text-[#1a2332] hover:bg-[#eef1f4]">Retry</button>
              </div>
            ) : !scoreView ? (
              <p className="text-sm text-muted">{SELECT_REVIEW_INCIDENT}</p>
            ) : (
              <div className="space-y-4" data-testid="learning-review-detail">
                {/* Header: incident + Assisted + achievements */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="t-overline">{INCIDENT_GRADE_LABEL}</p>
                    <h3 className="text-lg font-semibold text-[#1a2332]">
                      <span className="log-mono text-[#16436b] text-sm mr-2">{selectedId}</span>
                      {selectedCard?.title || ''}
                    </h3>
                  </div>
                  {scoreView.assisted && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#eef1f4] text-muted border border-[#d0d7de]">Assisted</span>
                  )}
                </div>
                <AchievementChips items={achievements} />

                {/* Grade rows (composite + components) */}
                <div className="space-y-1.5 text-sm">
                  {[['Classification', grading.classification], ['Detection dispositions', grading.detection], ['Response actions', grading.response]].map(([label, comp]) => (
                    <div key={label} className="flex items-center justify-between border-t border-[#eef1f4] pt-1.5">
                      <span className="text-muted">{label}</span>
                      <span className="font-medium text-[#1a2332]">{comp?.grade || '-'} · {comp?.accuracy ?? '-'}%</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t border-[#e2e6ea] pt-1.5">
                    <span className="text-[#1a2332] font-medium">Composite ({INCIDENT_GRADE_LABEL})</span>
                    <span className="font-semibold" style={{ color: gradeColor(grading.composite?.grade) }}>{grading.composite?.grade || '-'} · {grading.composite?.accuracy ?? '-'}%</span>
                  </div>
                </div>

                {/* The three acceptance questions (7.5), each with the
                    frozen whys; acceptable renders only when taken.
                    C1 checkpoint fix (F5a, ratified A1-B.4.1 item 3): the
                    well bucket includes correct dispositions alongside
                    completed required actions, and every empty state names
                    its own category (never a bare global negative). */}
                <div className="pt-3 border-t border-[#eef1f4]">
                  <p className="t-overline mb-1.5">{REVIEW_SECTION_CORRECT}</p>
                  <div className="space-y-2">
                    {correctCallCount > 0 ? (
                      <p className="text-sm text-[#1a2332]">{correctDetectionCalls(correctCallCount, detectionCalls.length)}</p>
                    ) : (
                      <p className="text-sm text-muted">{NO_CORRECT_CALLS}</p>
                    )}
                    {correctEntries.length === 0 ? (
                      <p className="text-sm text-muted">{NO_COMPLETED_REQUIRED}</p>
                    ) : (
                      <ul className="space-y-2">{correctEntries.map((e, i) => <EntryRow key={i} e={e} />)}</ul>
                    )}
                  </div>
                </div>
                <Section title={REVIEW_SECTION_MISSED} entries={missedEntries} emptyLine={REVIEW_EMPTY_MISSED} />
                <Section title={REVIEW_SECTION_COLLATERAL} entries={collateralEntries} emptyLine={REVIEW_EMPTY_COLLATERAL} />
                {/* Stage 6 (A4): the inaction-zero callout, adjacent to the
                    collateral it explains; frozen fields only. */}
                {review.inaction_zero && (
                  <div className="rounded-md border border-[#e4c5c5] bg-[#fdf6f6] px-3 py-2" data-testid="inaction-zero">
                    <p className="text-sm font-medium text-[#b45858]">{INACTION_ZERO_HEADING}</p>
                    <p className="text-sm text-[#1a2332] mt-0.5">
                      <span className="font-medium">{ACTION_LABELS[review.inaction_zero.action] || review.inaction_zero.action}</span>
                      {review.inaction_zero.target_label ? <span className="log-mono text-muted"> {review.inaction_zero.target_label}</span> : null}
                    </p>
                    <p className="text-sm text-muted mt-0.5">{review.inaction_zero.why}</p>
                  </div>
                )}
                {acceptableEntries.length > 0 && (
                  <Section title={REVIEW_SECTION_ACCEPTABLE} entries={acceptableEntries} />
                )}

                {/* Stage 6 (A4): the full expected response, disclosed only
                    post-submit from the frozen record -- required and
                    acceptable alike, including targets never executed. */}
                {review.expected_actions && (
                  <div className="pt-3 border-t border-[#eef1f4]" data-testid="expected-actions">
                    <p className="t-overline mb-1.5">{REVIEW_SECTION_EXPECTED}</p>
                    {review.expected_actions.length === 0 ? (
                      <p className="text-sm text-muted">{REVIEW_EMPTY_EXPECTED}</p>
                    ) : (
                      <ul className="space-y-1">
                        {review.expected_actions.map((x, i) => (
                          <li key={i} className="text-sm flex items-center gap-2">
                            <span className={`shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded border ${x.status === 'required'
                              ? 'bg-[#eef1f4] text-[#1a2332] border-[#d0d7de]'
                              : 'bg-white text-muted border-[#e2e6ea]'}`}>
                              {x.status === 'required' ? EXPECTED_REQUIRED_LABEL : EXPECTED_ACCEPTABLE_LABEL}
                            </span>
                            <span className="min-w-0 text-[#1a2332]">
                              <span className="font-medium">{ACTION_LABELS[x.action] || x.action}</span>
                              {x.target_label ? <span className="log-mono text-muted"> {x.target_label}</span> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Attempt history: factual, separately labeled, never a
                    teaching bucket (7.1). */}
                {(review.attempt_history || []).length > 0 && (
                  <div className="pt-3 border-t border-[#eef1f4]">
                    <p className="t-overline mb-1.5">{REVIEW_SECTION_ATTEMPTS}</p>
                    <ul className="space-y-1">
                      {review.attempt_history.map(h => (
                        <li key={h.seq} className="text-sm text-muted">
                          <span className="text-[#1a2332]">{ACTION_LABELS[h.action] || h.action}</span>
                          {h.target_label ? <span className="log-mono"> {h.target_label}</span> : null}
                          {' '}<span className="text-muted">({h.outcome === 'no_op' ? 'no effect, already in state' : 'did not execute'})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Per-detection verdicts from the frozen record */}
                <div className="pt-3 border-t border-[#eef1f4]">
                  <p className="t-overline mb-1.5">{REVIEW_SECTION_DETECTIONS}</p>
                  {(review.detections || []).length === 0 ? (
                    <p className="text-sm text-muted">{REVIEW_EMPTY_DETECTIONS}</p>
                  ) : (
                    <ul className="space-y-1">
                      {review.detections.map((d, i) => (
                        <li key={i} className="text-sm flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate text-[#1a2332]">{d.rule_name}
                            {d.entity_label ? <span className="text-muted"> · {d.entity_label}</span> : null}
                          </span>
                          <span className="shrink-0 text-xs">
                            <span className="text-muted">{d.your_call}</span>
                            {' '}<span style={{ color: d.correct ? '#6fa868' : '#b45858' }}>{d.correct ? CALL_CORRECT : CALL_WRONG}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Educational content (moved from the modal: one venue) */}
                {triage?.what_is_it && (
                  <div className="pt-3 border-t border-[#eef1f4]">
                    {triage.mitre && <p className="text-xs text-[#16436b] font-medium mb-1"><span className="log-mono">{triage.mitre.id}</span> · {triage.mitre.name}</p>}
                    <p className="text-sm font-medium text-[#1a2332]">{triage.what_is_it.title}</p>
                    <p className="text-sm text-muted mt-1 break-words">{triage.what_is_it.description}</p>
                  </div>
                )}
                {(triage?.response_actions || []).length > 0 && (
                  <div className="pt-3 border-t border-[#eef1f4]">
                    <p className="t-overline mb-1.5">{REVIEW_SECTION_PLAYBOOK}</p>
                    <ol className="list-decimal ml-5 space-y-1">
                      {triage.response_actions.map((step, i) => (
                        <li key={i} className="text-sm text-muted">{step}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Key takeaway: the frozen Tier 2 scenario rationale.
                    Ruling H: null -> section omitted (Tier 1 whys teach). */}
                {review.scenario_rationale && (
                  <div className="pt-3 border-t border-[#eef1f4]">
                    <p className="t-overline mb-1.5">{REVIEW_SECTION_TAKEAWAY}</p>
                    <p className="text-sm text-muted">{review.scenario_rationale}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningReview;
